import { Box, Typography, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import { FC, useState } from "react";

import { saveAppealLetter } from "../../../services/fileDownloadService";
import { useChatStore } from "../../../hooks/useChatStore";
import useNotifier from "../../../hooks/useNotifier";
import { AttachmentIcon } from "../../icons";
import Row from "../core/Row";
import FileRow from "./FileRow";
import FilesAttachedMenu from "./FilesAttachedMenu";
import LetterRow from "./LetterRow";
import SidebarAccordion from "./SidebarAccordion";
import { SIDEBAR_ACCORDION_FLEX_GROW } from "../../../types/sidebar";
import { useCapabilities } from "../../../contexts/permissions/useCapabilities";
import { IFileAttachment } from "../../../types/chat";
import RenameFileDialog from "./modals/RenameFileDialog";

const SidebarFileViewer: FC = observer(() => {
  const { canDownloadAttachments } = useCapabilities();
  const theme = useTheme();
  const chatStore = useChatStore();
  const notifier = useNotifier();
  const [isDownloadingLetter, setIsDownloadingLetter] = useState(false);
  const { files } = chatStore.currentChatFiles;
  const latestChatLetter = chatStore.latestChatLetter;
  const isRowActionsDisabled =
    chatStore.isClosed || chatStore.isReadOnly;

  /**
   * Both formats of the appeal letter, rendered by the backend (REV-2047).
   *
   * No flush is needed here, unlike the editor's buttons: this shows the stored
   * letter, so there are no pending edits to commit first. Stops at the first
   * failure rather than reporting twice for one cause.
   */
  const handleDownloadLetter = async () => {
    const chatId = chatStore.activeChatId;
    if (!chatId || isDownloadingLetter) return;

    setIsDownloadingLetter(true);
    try {
      // The export renders the stored letter, so an edit still in the
      // autosave debounce must land first or the file is a version behind.
      if (!(await chatStore.saveLetterEdit())) {
        notifier.showError(
          "Could not save your latest changes, so the download was cancelled. Check your connection and try again.",
        );
        return;
      }
      for (const format of ["pdf", "docx"] as const) {
        const result = await saveAppealLetter(
          chatId,
          format,
          chatStore.chatDetails?.claim_number,
        );
        if (!result.success) {
          notifier.showError(
            result.error ?? "Failed to download the appeal letter",
          );
          return;
        }
      }
    } finally {
      setIsDownloadingLetter(false);
    }
  };

  const handleFileClick = (
    file: NonNullable<typeof files>[number],
    originalFileName: string,
  ) => {
    chatStore.selectFileToPreview(file, originalFileName);
  };

  const handleToggleFilePin = async (file: IFileAttachment) => {
    const success = await chatStore.toggleFilePin(file);
    if (!success) {
      notifier.showError("Failed to update the file's pin status");
    }
  };

  const handleFileRename = (file: IFileAttachment, currentName: string) => {
    chatStore.setShowRenameFilePopup({ kind: "file", file, currentName });
  };

  const handleRenameFile = (newName: string) => {
    const target = chatStore.showRenameFilePopup;
    if (!target) return;

    if (target.kind === "file") {
      chatStore.applyFileRename(target.file, newName);
    }
  };

  const fileCount = files?.length ?? 0;
  const documentCount = latestChatLetter ? 1 : 0;
  const totalCount = fileCount + documentCount;

  if (totalCount === 0) {
    return null;
  }

  const pinnedFiles = files.filter((file) => file.is_pinned);
  const unpinnedFiles = files.filter((file) => !file.is_pinned);
  // A pinned letter moves out of GENERATED into PINNED, after the pinned files,
  // and its HTML moves with it into that section's Download All.
  const isLetterPinned = !!latestChatLetter?.is_pinned;
  const pinnedCount = pinnedFiles.length + (isLetterPinned ? 1 : 0);
  const hasPinnedSection = pinnedCount > 0;

  return (
    <>
      <SidebarAccordion
        title="Documents"
        icon={
          <AttachmentIcon
            width={16}
            height={16}
            className="shrink-0"
            style={{ color: theme.palette.custom.brandTeal }}
          />
        }
        flexGrow={SIDEBAR_ACCORDION_FLEX_GROW}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
            px: 1,
          }}
        >
          {hasPinnedSection && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                width: "100%",
              }}
            >
              <Row
                id="pinned-files-content"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    px: 1,
                    py: 0.5,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  PINNED({pinnedCount})
                </Typography>

                {canDownloadAttachments ? (
                  <FilesAttachedMenu
                    files={pinnedFiles}
                    letter={isLetterPinned ? latestChatLetter : undefined}
                    claimId={chatStore.chatDetails?.claim_number}
                    chatId={chatStore.activeChatId}
                  />
                ) : null}
              </Row>

              {pinnedFiles.map((file, index) => (
                <FileRow
                  key={file.id || index}
                  file={file}
                  index={index}
                  canDownloadAttachments={canDownloadAttachments}
                  isClosed={isRowActionsDisabled}
                  onSelect={handleFileClick}
                  onTogglePin={handleToggleFilePin}
                  onRename={handleFileRename}
                />
              ))}

              {isLetterPinned &&
                (latestChatLetter ? (
                  <LetterRow
                    letter={latestChatLetter}
                    isDownloading={isDownloadingLetter}
                    isClosed={isRowActionsDisabled}
                    onSelect={() =>
                      chatStore.setLetterToPreview(latestChatLetter)
                    }
                    onDownload={handleDownloadLetter}
                  />
                ) : null)}
            </Box>
          )}

          {unpinnedFiles.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                width: "100%",
              }}
            >
              <Row
                id="file-viewer-content"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    px: 1,
                    py: 0.5,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  USER ATTACHED({unpinnedFiles.length})
                </Typography>

                {canDownloadAttachments ? (
                  <FilesAttachedMenu
                    files={unpinnedFiles}
                    letter={isLetterPinned ? undefined : latestChatLetter}
                    claimId={chatStore.chatDetails?.claim_number}
                    chatId={chatStore.activeChatId}
                  />
                ) : null}
              </Row>

              {unpinnedFiles.map((file, index) => (
                <FileRow
                  key={file.id || index}
                  file={file}
                  index={index}
                  canDownloadAttachments={canDownloadAttachments}
                  isClosed={isRowActionsDisabled}
                  onSelect={handleFileClick}
                  onTogglePin={handleToggleFilePin}
                  onRename={handleFileRename}
                />
              ))}
            </Box>
          )}

          {latestChatLetter && !isLetterPinned && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                width: "100%",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  px: 1,
                  py: 0.5,
                }}
              >
                GENERATED (1)
              </Typography>

              {latestChatLetter ? (
                <LetterRow
                  letter={latestChatLetter}
                  isDownloading={isDownloadingLetter}
                  isClosed={isRowActionsDisabled}
                  onSelect={() =>
                    chatStore.setLetterToPreview(latestChatLetter)
                  }
                  onDownload={handleDownloadLetter}
                />
              ) : null}
            </Box>
          )}
        </Box>
      </SidebarAccordion>

      <RenameFileDialog
        target={chatStore.showRenameFilePopup}
        chatId={chatStore.activeChatId || ""}
        onClose={() => chatStore.setShowRenameFilePopup(null)}
        onConfirmCallback={handleRenameFile}
      />
    </>
  );
});

export default SidebarFileViewer;
